import { refreshAdmin } from './admin-auth-api';
import { useAdminAuthStore } from '../stores/useAdminAuthStore';

export interface DailyReportParseResponse {
  caption: string;
  source_file: string;
  extraction_mode: string;
  issue_no?: string | null;
  publish_time?: string | null;
  station_rows_text?: string | null;
  units_text?: string | null;
  weather_text: string;
  advice_text: string;
  hazard_text: string;
  extracted_text: string;
  raw_warnings: string[];
  sms_text: string;
  word_style_text: string;
}

export interface DailyReportSubmitResponse {
  updated_count: number;
  weather_text: string;
  caption_text: string;
}

interface RequestPayload {
  file: File;
  caption: string;
}

async function readErrorMessage(response: Response): Promise<string> {
  const fallback = response.statusText || 'Request failed.';
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    try {
      const payload = (await response.json()) as { detail?: unknown };
      if (typeof payload.detail === 'string' && payload.detail.trim()) {
        return payload.detail;
      }
      if (payload.detail != null) {
        return JSON.stringify(payload.detail);
      }
      return JSON.stringify(payload);
    } catch {
      return fallback;
    }
  }

  const text = await response.text().catch(() => fallback);
  return text || fallback;
}

async function requestDailyReport<T>(
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const response = await fetch(input, init);

  return parseDailyReportResponse<T>(response, actionLabel);
}

async function parseDailyReportResponse<T>(
  response: Response,
  actionLabel: string,
): Promise<T> {
  if (!response.ok) {
    const message = await readErrorMessage(response);
    throw new Error(`${actionLabel} failed (${response.status}): ${message}`);
  }

  return response.json() as Promise<T>;
}

let adminSessionRefreshPromise: Promise<string | null> | null = null;

async function refreshAdminAccessToken(): Promise<string | null> {
  if (adminSessionRefreshPromise) {
    return adminSessionRefreshPromise;
  }

  adminSessionRefreshPromise = refreshAdmin()
    .then((session) => {
      useAdminAuthStore.getState().setSession(session.accessToken, session.user);
      return session.accessToken;
    })
    .catch(() => {
      useAdminAuthStore.getState().clearSession();
      return null;
    })
    .finally(() => {
      adminSessionRefreshPromise = null;
    });

  return adminSessionRefreshPromise;
}

async function requestAdminDailyReport<T>(
  accessToken: string,
  input: RequestInfo | URL,
  init: RequestInit,
  actionLabel: string,
): Promise<T> {
  const send = (token: string) =>
    fetch(input, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });

  let response = await send(accessToken);
  if (response.status === 401) {
    const refreshedAccessToken = await refreshAdminAccessToken();
    if (refreshedAccessToken) {
      response = await send(refreshedAccessToken);
    }
  }

  return parseDailyReportResponse<T>(response, actionLabel);
}

export async function parseDailyReport(input: RequestPayload): Promise<DailyReportParseResponse> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('caption', input.caption);

  return requestDailyReport<DailyReportParseResponse>(
    '/api/v1/daily-report/parse',
    {
      method: 'POST',
      body: formData,
    },
    'Parse daily report',
  );
}

export async function submitAvomanDailyUpdate(accessToken: string, input: {
  weatherText: string;
  captionText: string;
  sourceFile?: string | null;
}): Promise<DailyReportSubmitResponse> {
  return requestAdminDailyReport<DailyReportSubmitResponse>(
    accessToken,
    '/api/v1/daily-report/submit-avoman-daily-update',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        weather_text: input.weatherText,
        caption_text: input.captionText,
        source_file: input.sourceFile ?? null,
      }),
    },
    'Submit daily report',
  );
}
